/* Product relationships come from the existing theme recommendation groups.
 * Catalog prices are integer minor units; maxPrice is exclusive (5000 = under $50).
 * The engine never fetches, changes the cart or substitutes a chosen variant.
 */
(() => {
  'use strict';

  const ignoredCollections = new Set(['all', 'all-products', 'shop-all', 'featured', 'frontpage']);
  const broadCollections = new Set(['home-organization', 'lighting-decor', 'decor-display', 'living-room', 'bedroom']);
  const identifier = value => String(value ?? '').split('/').pop();
  const phrase = value => `-${String(value || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-`;
  const collectionHandles = product => (product.collections || []).map(value => typeof value === 'string' ? value : value.handle).filter(value => value && !ignoredCollections.has(value));

  class RecommendationEngine {
    constructor(rules = {}) {
      this.groups = Array.isArray(rules) ? rules : rules.groups || [];
      this.exact = new Map();
      this.groups.forEach(group => (group.products || []).forEach(handle => {
        if (!this.exact.has(handle)) this.exact.set(handle, group);
      }));
      this.compiled = this.groups.map(group => ({ group, terms: (group.terms || []).map(phrase) }));
      this.relationships = new Map(this.groups.map(group => [group.id, {
        paired: new Map((group.paired || []).map((handle, index) => [handle, index])),
        similar: new Map((group.similar || []).map((handle, index) => [handle, index])),
        pairedGroups: new Set((group.paired || []).map(handle => this.exact.get(handle)?.id).filter(Boolean)),
        similarGroups: new Set((group.similar || []).map(handle => this.exact.get(handle)?.id).filter(Boolean)),
      }]));
    }

    classify(product) {
      if (!product) return null;
      if (this.exact.has(product.handle)) return this.exact.get(product.handle);
      const title = phrase(product.title);
      const type = phrase(product.type || product.productType || product.product_type);
      let best = null;
      let score = 0;
      let ambiguous = false;
      this.compiled.forEach(({ group, terms }) => {
        let strength = 0;
        terms.forEach(term => {
          if (term !== '--' && (title.includes(term) || type.includes(term))) strength = Math.max(strength, term.length - 2);
        });
        if (strength > score) {
          best = group;
          score = strength;
          ambiguous = false;
        } else if (strength > 0 && strength === score) {
          ambiguous = true;
        }
      });
      if (ambiguous) return null;
      if (score > 0) return best;
      const collections = new Set(collectionHandles(product));
      const fallback = this.groups.filter(group => group.matchCollection && collections.has(group.matchCollection));
      return fallback.length === 1 ? fallback[0] : null;
    }

    eligibleVariants(product, maxPrice = Infinity) {
      return (product?.variants || []).filter(variant => variant.available === true && Number(variant.price) > 0 && Number(variant.price) < maxPrice);
    }

    rank(catalog, seeds, options = {}) {
      const mode = options.mode === 'similar' ? 'similar' : 'paired';
      const limit = Math.max(0, Math.floor(options.limit ?? 5));
      const maxPrice = options.maxPrice == null ? Infinity : Number(options.maxPrice);
      if (!Array.isArray(catalog) || !limit || !(maxPrice > 0)) return [];
      const byId = new Map(catalog.map(product => [identifier(product.id), product]));
      const byHandle = new Map(catalog.map(product => [product.handle, product]));
      const excluded = new Set();
      const exclude = value => {
        if (value && typeof value === 'object') {
          if (value.id != null) excluded.add(identifier(value.id));
          if (value.handle) excluded.add(value.handle);
        } else if (value != null) {
          excluded.add(identifier(value));
        }
      };
      (options.exclude || []).forEach(exclude);
      const seedKeys = new Set();
      const seedProducts = (Array.isArray(seeds) ? seeds : [seeds]).filter(Boolean).map(seed => {
        if (typeof seed !== 'object') return byId.get(identifier(seed)) || byHandle.get(seed);
        return byId.get(identifier(seed.id)) || byHandle.get(seed.handle) || seed;
      }).filter(seed => {
        if (!seed) return false;
        exclude(seed);
        const key = this.classify(seed)?.id || seed.handle || identifier(seed.id);
        if (seedKeys.has(key)) return false;
        seedKeys.add(key);
        return true;
      });
      const eligible = [];
      const candidateKeys = new Set();
      catalog.forEach((product, index) => {
        const key = identifier(product.id) || product.handle;
        if (!key || candidateKeys.has(key) || excluded.has(key) || excluded.has(product.handle) || !this.eligibleVariants(product, maxPrice).length) return;
        candidateKeys.add(key);
        eligible.push({ product, index, group: this.classify(product), collections: new Set(collectionHandles(product)) });
      });

      const queues = seedProducts.map(seed => {
        const group = this.classify(seed);
        const relationships = group ? this.relationships.get(group.id) : null;
        const sourceCollections = collectionHandles(seed);
        return eligible.map(candidate => {
          const handle = candidate.product.handle;
          let score = 0;
          if (relationships) {
            const first = mode === 'paired' ? relationships.paired : relationships.similar;
            const second = mode === 'paired' ? relationships.similar : relationships.paired;
            if (first.has(handle)) score = Math.max(score, 1000 - first.get(handle) * 4);
            if (second.has(handle)) score = Math.max(score, 550 - second.get(handle) * 3);
            const primaryGroups = mode === 'paired' ? relationships.pairedGroups : relationships.similarGroups;
            const secondaryGroups = mode === 'paired' ? relationships.similarGroups : relationships.pairedGroups;
            if (candidate.group && primaryGroups.has(candidate.group.id)) score = Math.max(score, 700);
            if (candidate.group && secondaryGroups.has(candidate.group.id)) score = Math.max(score, 400);
            if (candidate.group?.id === group.id) score = Math.max(score, mode === 'similar' ? 850 : 150);
            const preferredCollections = mode === 'paired' ? group.pairedCollections : group.similarCollections;
            if ((preferredCollections || []).some(value => candidate.collections.has(value))) score = Math.max(score, 650);
          }
          const overlap = sourceCollections.filter(value => candidate.collections.has(value));
          if (overlap.length) {
            const collectionScore = overlap.reduce((sum, value) => sum + (broadCollections.has(value) ? 30 : 80), 0);
            score = Math.max(score, Math.min(300, collectionScore));
          }
          return { ...candidate, score };
        }).filter(candidate => candidate.score > 0).sort((a, b) => b.score - a.score || a.index - b.index);
      });

      const selected = [];
      const selectedKeys = new Set();
      let progress = true;
      while (selected.length < limit && progress) {
        progress = false;
        for (const queue of queues) {
          while (queue.length && selectedKeys.has(identifier(queue[0].product.id) || queue[0].product.handle)) queue.shift();
          const candidate = queue.shift();
          if (!candidate) continue;
          const key = identifier(candidate.product.id) || candidate.product.handle;
          selectedKeys.add(key);
          selected.push(candidate.product);
          progress = true;
          if (selected.length >= limit) break;
        }
      }
      if (options.allowBroader === true && mode === 'similar') {
        for (const candidate of eligible) {
          if (selected.length >= limit) break;
          const key = identifier(candidate.product.id) || candidate.product.handle;
          if (selectedKeys.has(key)) continue;
          selectedKeys.add(key);
          selected.push(candidate.product);
        }
      }
      return selected;
    }
  }

  window.PlainSightRecommendations = {
    create: rules => new RecommendationEngine(rules),
    Engine: RecommendationEngine,
  };
})();
